import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../config/api';
import '../styles/BatchDetail.css';

interface Collaboration {
  _id: string;
  handle: string;
  email: string;
  platform: string;
  agreedPrice: number;
  postMetrics?: {
    primaryViews?: number;
    cpm?: number;
  };
  totalViews?: number;
}

interface BatchCollaboration {
  _id: string;
  handle: string;
  email: string;
  batchSequence: number;
  totalBatchVideos: number;
  collaborationStage: string;
  agreedPrice: number;
  createdAt: string;
  deadline?: string;
  postUrlTikTok?: string;
  postUrlInstagram?: string;
  postUrlYoutube?: string;
}

interface BatchData {
  _id: string;
  handle: string;
  primaryPlatform: string;
  averageCollaborationViews: number;
  lastCollaborationViews: number;
  batchPrice: number;
  batchSize: number;
  batchCount: number;
  status: 'pending' | 'accepted' | 'negotiating' | 'rejected';
  negotiatedPrice?: number;
  negotiatedPackageSize?: number;
  emailSent: boolean;
  lastEmailSentAt?: string;
  responseDate?: string;
  collaborationId: Collaboration;
  createdAt: string;
  updatedAt: string;
  skipScriptStage?: boolean;
}

const BatchDetail: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const [batch, setBatch] = useState<BatchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCounterOfferModal, setShowCounterOfferModal] = useState(false);
  const [adminCounterPrice, setAdminCounterPrice] = useState<string>('');
  const [adminCounterPackageSize, setAdminCounterPackageSize] = useState<string>('');
  const [processingAction, setProcessingAction] = useState(false);
  const [batchCollaborations, setBatchCollaborations] = useState<BatchCollaboration[]>([]);
  const [loadingCollaborations, setLoadingCollaborations] = useState(false);
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [selectedCollaborationId, setSelectedCollaborationId] = useState<string>('');
  const [topicInput, setTopicInput] = useState<string>('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    creatorEmail: '',
    batchPrice: '',
    batchSize: '',
    rpmRate: '',
    maxPrice: ''
  });

  useEffect(() => {
    fetchBatchDetail();
  }, [batchId]);

  const fetchBatchDetail = async () => {
    try {
      const response = await fetch(`${API_URL}/api/batch/proposal/${batchId}`);
      const result = await response.json();
      
      if (result.success) {
        setBatch(result.data);
        // Fetch collaborations if batch is accepted
        if (result.data.status === 'accepted') {
          fetchBatchCollaborations();
        }
      } else {
        setError(result.error || 'Failed to load batch details');
      }
    } catch (err) {
      setError('Failed to load batch details');
    } finally {
      setLoading(false);
    }
  };

  const fetchBatchCollaborations = async () => {
    setLoadingCollaborations(true);
    try {
      const response = await fetch(`${API_URL}/api/batch/${batchId}/collaborations`);
      const result = await response.json();
      
      if (result.success) {
        setBatchCollaborations(result.data);
      }
    } catch (err) {
      console.error('Failed to fetch batch collaborations:', err);
    } finally {
      setLoadingCollaborations(false);
    }
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString('en-US');
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { label: string; color: string; bg: string } } = {
      pending: { label: 'Pending', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
      accepted: { label: 'Accepted', color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
      negotiating: { label: 'Negotiating', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' },
      rejected: { label: 'Rejected', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span 
        className="status-badge"
        style={{ 
          background: config.bg,
          color: config.color,
          padding: '6px 12px',
          borderRadius: '6px',
          fontWeight: '600'
        }}
      >
        {config.label}
      </span>
    );
  };

  const getCollaborationStatus = (stage: string) => {
    // Derive status from collaborationStage
    if (stage === 'completed') {
      return { label: 'Completed', color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' };
    } else if (stage === 'scheduled') {
      return { label: 'Scheduled', color: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)' };
    } else {
      return { label: 'Active', color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' };
    }
  };

  const getCollaborationStatusBadge = (stage: string) => {
    const config = getCollaborationStatus(stage);

    return (
      <span style={{
        background: config.bg,
        color: config.color,
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: '600'
      }}>
        {config.label}
      </span>
    );
  };

  const getCollaborationStageBadge = (stage: string) => {
    const stageConfig: { [key: string]: { label: string; color: string } } = {
      scheduled: { label: 'Scheduled', color: '#6b7280' },
      awaiting_script: { label: 'Awaiting Script', color: '#f59e0b' },
      reviewing_script: { label: 'Reviewing Script', color: '#3b82f6' },
      awaiting_video: { label: 'Awaiting Video', color: '#f59e0b' },
      reviewing_video: { label: 'Reviewing Video', color: '#3b82f6' },
      awaiting_post: { label: 'Awaiting Post', color: '#f59e0b' },
      reviewing_post: { label: 'Reviewing Post', color: '#3b82f6' },
      awaiting_payment_info: { label: 'Payment Info', color: '#8b5cf6' },
      processing_payment: { label: 'Processing Payment', color: '#8b5cf6' },
      completed: { label: 'Completed', color: '#10b981' }
    };

    const config = stageConfig[stage] || { label: stage, color: '#6b7280' };
    
    return (
      <span style={{ 
        color: config.color,
        fontSize: '13px',
        fontWeight: '500'
      }}>
        {config.label}
      </span>
    );
  };

  const handleAcceptCounterOffer = async () => {
    if (!batch) return;
    
    setProcessingAction(true);
    try {
      const response = await fetch(`${API_URL}/api/batch/counter-offer/${batch._id}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setBatch(result.data);
        alert('Counter offer accepted successfully!');
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error accepting counter offer:', error);
      alert('Failed to accept counter offer');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRejectCounterOffer = async () => {
    if (!batch) return;
    
    if (!window.confirm('Are you sure you want to reject this counter offer?')) {
      return;
    }

    setProcessingAction(true);
    try {
      const response = await fetch(`${API_URL}/api/batch/counter-offer/${batch._id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setBatch(result.data);
        alert('Counter offer rejected');
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error rejecting counter offer:', error);
      alert('Failed to reject counter offer');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleMakeCounterOffer = async () => {
    if (!batch || !adminCounterPrice) return;
    
    const price = parseFloat(adminCounterPrice);
    const packageSize = parseInt(adminCounterPackageSize) || batch.batchSize;
    
    if (isNaN(price) || price <= 0) {
      alert('Please enter a valid price');
      return;
    }
    
    if (packageSize <= 0) {
      alert('Please enter a valid package size');
      return;
    }

    setProcessingAction(true);
    try {
      const response = await fetch(`${API_URL}/api/batch/counter-offer/${batch._id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          adminCounterPrice: price,
          adminCounterPackageSize: packageSize
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setBatch(result.data);
        setShowCounterOfferModal(false);
        setAdminCounterPrice('');
        setAdminCounterPackageSize('');
        alert('Counter offer sent to creator');
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error making counter offer:', error);
      alert('Failed to make counter offer');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleStartCollaboration = (collaborationId: string) => {
    setSelectedCollaborationId(collaborationId);
    setShowTopicModal(true);
  };

  const handleResendEmail = async () => {
    if (!batch) return;

    if (!window.confirm('Are you sure you want to resend the proposal email?')) {
      return;
    }

    setProcessingAction(true);
    try {
      const response = await fetch(`${API_URL}/api/batch/${batch._id}/resend-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const result = await response.json();

      if (result.success) {
        fetchBatchDetail();
        alert(result.message || 'Email sent successfully!');
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error resending email:', error);
      alert('Failed to resend email');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleOpenEditModal = () => {
    if (!batch) return;
    setEditForm({
      creatorEmail: batch.collaborationId?.email || '',
      batchPrice: batch.batchPrice?.toString() || '',
      batchSize: batch.batchSize?.toString() || '',
      rpmRate: (batch as any).rpmRate?.toString() || '',
      maxPrice: (batch as any).maxPrice?.toString() || ''
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (field: string, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleToggleSkipScriptStage = async () => {
    if (!batch) return;

    setProcessingAction(true);
    try {
      const response = await fetch(`${API_URL}/api/batch/${batch._id}/skip-script-stage`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skipScriptStage: !batch.skipScriptStage })
      });

      const result = await response.json();

      if (result.success) {
        setBatch(prev => prev ? { ...prev, skipScriptStage: !prev.skipScriptStage } : null);
        alert(`Skip Script Stage ${!batch.skipScriptStage ? 'enabled' : 'disabled'} successfully!`);
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error toggling skip script stage:', error);
      alert('Failed to toggle skip script stage');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!batch) return;

    setProcessingAction(true);
    try {
      const updateData: any = {};

      if (editForm.creatorEmail && editForm.creatorEmail !== batch.collaborationId?.email) {
        updateData.creatorEmail = editForm.creatorEmail;
      }
      if (editForm.batchPrice && parseInt(editForm.batchPrice) !== batch.batchPrice) {
        updateData.batchPrice = parseInt(editForm.batchPrice);
      }
      if (editForm.batchSize && parseInt(editForm.batchSize) !== batch.batchSize) {
        updateData.batchSize = parseInt(editForm.batchSize);
      }
      if (editForm.rpmRate) {
        updateData.rpmRate = parseFloat(editForm.rpmRate);
      }
      if (editForm.maxPrice) {
        updateData.maxPrice = parseInt(editForm.maxPrice);
      }

      if (Object.keys(updateData).length === 0) {
        alert('No changes to save');
        setShowEditModal(false);
        setProcessingAction(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/batch/${batch._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      const result = await response.json();

      if (result.success) {
        setBatch(result.data);
        setShowEditModal(false);
        alert('Batch updated successfully!');
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error updating batch:', error);
      alert('Failed to update batch');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleConfirmStartCollaboration = async () => {
    if (!selectedCollaborationId) return;

    setProcessingAction(true);
    try {
      const response = await fetch(`${API_URL}/api/collaborations/${selectedCollaborationId}/start-collaboration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          topic: topicInput.trim() || undefined 
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Refresh collaborations list
        fetchBatchCollaborations();
        setShowTopicModal(false);
        setSelectedCollaborationId('');
        setTopicInput('');
        alert('Collaboration started successfully!');
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error starting collaboration:', error);
      alert('Failed to start collaboration');
    } finally {
      setProcessingAction(false);
    }
  };

  if (loading) {
    return (
      <div className="batch-detail-container">
        <div className="loading">Loading batch details...</div>
      </div>
    );
  }

  if (error || !batch) {
    return (
      <div className="batch-detail-container">
        <div className="error-message">
          <h2>Batch Not Found</h2>
          <p>{error || 'This batch may not exist.'}</p>
          <button onClick={() => navigate('/batch')}>Back to Batches</button>
        </div>
      </div>
    );
  }

  const totalPackageValue = batch.batchPrice * batch.batchSize;
  const originalPrice = batch.collaborationId.agreedPrice;
  const discount = Math.round(((originalPrice - batch.batchPrice) / originalPrice) * 100);

  return (
    <div className="batch-detail-container">
      <div className="detail-header">
        <button className="back-button" onClick={() => navigate('/batch')}>
          ← Back to Batches
        </button>
        <h1>Batch Details</h1>
      </div>

      <div className="detail-grid">
        {/* Creator Information */}
        <div className="detail-card">
          <h3>Creator Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Handle</label>
              <span>{batch.handle}</span>
            </div>
            <div className="info-item">
              <label>Email</label>
              <span>{batch.collaborationId.email}</span>
            </div>
            <div className="info-item">
              <label>Platform</label>
              <span className="platform-badge">{batch.primaryPlatform}</span>
            </div>
            <div className="info-item">
              <label>Batch Number</label>
              <span>#{batch.batchCount}</span>
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="detail-card">
          <h3>Performance Metrics</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Average Views</label>
              <span className="metric-value">{formatNumber(batch.averageCollaborationViews)}</span>
            </div>
            <div className="info-item">
              <label>Last Collaboration Views</label>
              <span className="metric-value">{formatNumber(batch.lastCollaborationViews)}</span>
            </div>
            {batch.collaborationId.postMetrics?.cpm && (
              <div className="info-item">
                <label>CPM</label>
                <span className="metric-value">${batch.collaborationId.postMetrics.cpm.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Proposal Details */}
        <div className="detail-card">
          <h3>Proposal Details</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Status</label>
              {getStatusBadge(batch.status)}
            </div>
            <div className="info-item">
              <label>Batch Size</label>
              <span>{batch.batchSize} videos</span>
            </div>
            <div className="info-item">
              <label>Price per Video</label>
              <span>
                ${batch.batchPrice}
                <span className="discount-badge">{discount}% off</span>
              </span>
            </div>
            <div className="info-item">
              <label>Original Price</label>
              <span>${originalPrice}</span>
            </div>
            <div className="info-item">
              <label>Total Package Value</label>
              <span className="total-value">${totalPackageValue}</span>
            </div>
          </div>
        </div>

        {/* Negotiation Details (if applicable) */}
        {(batch.status === 'negotiating' && batch.negotiatedPrice) && (
          <div className="detail-card">
            <h3>Counter Offer</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Proposed Price</label>
                <span>${batch.negotiatedPrice} per video</span>
              </div>
              {batch.negotiatedPackageSize && (
                <div className="info-item">
                  <label>Proposed Package Size</label>
                  <span>{batch.negotiatedPackageSize} videos</span>
                </div>
              )}
              <div className="info-item">
                <label>Proposed Total</label>
                <span className="total-value">
                  ${(batch.negotiatedPrice || 0) * (batch.negotiatedPackageSize || batch.batchSize)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Rejection Details (if applicable) */}
        {batch.status === 'rejected' && (
          <div className="detail-card rejection-card" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
            <h3 style={{ color: '#991b1b' }}>🚫 Proposal Declined</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Declined On</label>
                <span>{batch.responseDate ? formatDate(batch.responseDate) : 'Unknown'}</span>
              </div>
              <div className="info-item">
                <label>Status</label>
                <span style={{ color: '#991b1b', fontWeight: '600' }}>This proposal was declined by the creator</span>
              </div>
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="detail-card">
          <h3>Timeline</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Created</label>
              <span>{formatDate(batch.createdAt)}</span>
            </div>
            <div className="info-item">
              <label>Email Sent</label>
              <span>
                {batch.emailSent ? (
                  batch.lastEmailSentAt ? (
                    `✅ ${formatDate(batch.lastEmailSentAt)}`
                  ) : (
                    '✅ Yes'
                  )
                ) : (
                  '❌ No'
                )}
              </span>
            </div>
            {batch.responseDate && (
              <div className="info-item">
                <label>Response Date</label>
                <span>{formatDate(batch.responseDate)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Settings */}
        <div className="detail-card">
          <h3>Settings</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              padding: '12px 16px',
              background: batch.skipScriptStage ? 'rgba(16, 185, 129, 0.1)' : '#f3f4f6',
              border: batch.skipScriptStage ? '2px solid #10b981' : '2px solid #e5e7eb',
              borderRadius: '8px',
              transition: 'all 0.2s ease'
            }}>
              <input
                type="checkbox"
                checked={batch.skipScriptStage || false}
                onChange={handleToggleSkipScriptStage}
                disabled={processingAction}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer',
                  accentColor: '#10b981'
                }}
              />
              <div>
                <div style={{
                  fontWeight: '600',
                  color: batch.skipScriptStage ? '#10b981' : '#374151',
                  fontSize: '14px'
                }}>
                  Skip Script Stage
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                  Go directly to video submission
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="detail-card actions-card">
          <h3>Actions</h3>
          <div className="action-buttons">
            <button
              className="btn-edit"
              onClick={handleOpenEditModal}
              style={{
                background: '#8b5cf6',
                color: 'white',
                border: 'none',
                padding: '10px 16px',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ✏️ Edit Batch
            </button>
            <button
              className="btn-view-creator"
              onClick={() => window.open(`/batch-creator/${batch._id}`, '_blank')}
            >
              View Creator Page
            </button>
            <button
              className="btn-view-collaboration"
              onClick={() => navigate(`/collaborations/${batch.collaborationId._id}`)}
            >
              View Original Collaboration
            </button>
            {batch.status === 'pending' && (
              <button
                className="btn-resend-email"
                onClick={handleResendEmail}
                disabled={processingAction}
                style={{
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: processingAction ? 'not-allowed' : 'pointer',
                  opacity: processingAction ? 0.6 : 1
                }}
              >
                {processingAction ? 'Sending...' : '📧 Resend Email'}
              </button>
            )}
          </div>
        </div>

        {/* Admin Counter Offer Actions */}
        {batch.status === 'negotiating' && batch.negotiatedPrice && (
          <div className="detail-card admin-actions-card" style={{ background: '#f0f9ff', border: '2px solid #3b82f6' }}>
            <h3 style={{ color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '8px' }}>
              💼 Admin Actions
              <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#64748b' }}>
                (Counter offer received)
              </span>
            </h3>
            
            <div style={{ marginBottom: '16px', padding: '12px', background: '#dbeafe', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#1d4ed8', fontWeight: '600' }}>Creator's Counter Offer:</span>
                <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1d4ed8' }}>
                  ${batch.negotiatedPrice} per video
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                <span style={{ color: '#64748b', fontSize: '14px' }}>Original offer:</span>
                <span style={{ fontSize: '14px', color: '#64748b' }}>
                  ${batch.batchPrice} per video
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                <span style={{ color: '#64748b', fontSize: '14px' }}>Total package value:</span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#1d4ed8' }}>
                  ${(batch.negotiatedPrice * batch.batchSize).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="admin-action-buttons" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button 
                className="btn-accept-counter"
                onClick={handleAcceptCounterOffer}
                disabled={processingAction}
                style={{
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  opacity: processingAction ? 0.6 : 1
                }}
              >
                ✅ Accept Counter Offer
              </button>
              
              <button 
                className="btn-make-counter"
                onClick={() => setShowCounterOfferModal(true)}
                disabled={processingAction}
                style={{
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  opacity: processingAction ? 0.6 : 1
                }}
              >
                💬 Make Counter Offer
              </button>
              
              <button 
                className="btn-reject-counter"
                onClick={handleRejectCounterOffer}
                disabled={processingAction}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  opacity: processingAction ? 0.6 : 1
                }}
              >
                ❌ Reject Counter Offer
              </button>
            </div>
          </div>
        )}

        {/* Batch Collaborations (if accepted) */}
        {batch.status === 'accepted' && (
          <div className="detail-card" style={{ gridColumn: 'span 2' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              📋 Batch Collaborations
              <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#6b7280' }}>
                ({batchCollaborations.length}/{batch.batchSize} videos)
              </span>
            </h3>
            
            {loadingCollaborations ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                Loading collaborations...
              </div>
            ) : batchCollaborations.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Video #</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Status</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Stage</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Post Links</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchCollaborations.map((collab) => (
                      <tr key={collab._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px' }}>
                          <span style={{ fontWeight: '600', color: '#1f2937' }}>
                            Video {collab.batchSequence}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {getCollaborationStatusBadge(collab.collaborationStage)}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {getCollaborationStageBadge(collab.collaborationStage)}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {collab.postUrlTikTok && (
                              <a href={collab.postUrlTikTok} target="_blank" rel="noopener noreferrer" 
                                 style={{ color: '#3b82f6', fontSize: '12px' }}>TikTok</a>
                            )}
                            {collab.postUrlInstagram && (
                              <a href={collab.postUrlInstagram} target="_blank" rel="noopener noreferrer"
                                 style={{ color: '#3b82f6', fontSize: '12px' }}>Instagram</a>
                            )}
                            {collab.postUrlYoutube && (
                              <a href={collab.postUrlYoutube} target="_blank" rel="noopener noreferrer"
                                 style={{ color: '#3b82f6', fontSize: '12px' }}>YouTube</a>
                            )}
                            {!collab.postUrlTikTok && !collab.postUrlInstagram && !collab.postUrlYoutube && (
                              <span style={{ color: '#9ca3af', fontSize: '12px' }}>No posts yet</span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              onClick={() => navigate(`/collaborations/${collab._id}`)}
                              style={{
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                fontWeight: '500'
                              }}
                            >
                              View Details
                            </button>
                            {collab.collaborationStage === 'scheduled' && (
                              <button
                                onClick={() => handleStartCollaboration(collab._id)}
                                style={{
                                  background: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  padding: '6px 12px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  fontWeight: '500'
                                }}
                              >
                                Collaborate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ 
                padding: '40px', 
                textAlign: 'center', 
                background: '#f9fafb', 
                borderRadius: '8px',
                color: '#6b7280'
              }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '500' }}>
                  No collaborations created yet
                </p>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  Collaborations will be created once the batch setup is complete
                </p>
              </div>
            )}
            
            {/* Progress Summary */}
            {batchCollaborations.length > 0 && (
              <div style={{ 
                marginTop: '20px', 
                padding: '16px', 
                background: '#f0f9ff', 
                borderRadius: '8px',
                border: '1px solid #bae6fd'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Completed</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#10b981' }}>
                      {batchCollaborations.filter(c => c.collaborationStage === 'completed').length}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>In Progress</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#f59e0b' }}>
                      {batchCollaborations.filter(c => c.collaborationStage !== 'scheduled' && c.collaborationStage !== 'completed').length}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Scheduled</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#6b7280' }}>
                      {batchCollaborations.filter(c => c.collaborationStage === 'scheduled').length}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Total Value</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>
                      ${((batch.negotiatedPrice || batch.batchPrice) * batchCollaborations.length).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Counter Offer Modal */}
      {showCounterOfferModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginTop: 0, color: '#1d4ed8' }}>💬 Make Counter Offer</h3>
            
            <div style={{ marginBottom: '16px' }}>
              <div style={{ marginBottom: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                <p style={{ color: '#64748b', margin: '0 0 4px 0', fontSize: '14px' }}>
                  Creator's counter offer:
                </p>
                <p style={{ color: '#1d4ed8', margin: '0 0 4px 0', fontSize: '16px', fontWeight: 'bold' }}>
                  ${batch?.negotiatedPrice} per video × {batch?.negotiatedPackageSize || batch?.batchSize} videos
                </p>
                <p style={{ color: '#64748b', margin: '0', fontSize: '14px' }}>
                  Total: ${((batch?.negotiatedPrice || 0) * (batch?.negotiatedPackageSize || batch?.batchSize || 0)).toLocaleString()}
                </p>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', color: '#374151', fontWeight: '600' }}>
                    Price per Video ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={adminCounterPrice}
                    onChange={(e) => setAdminCounterPrice(e.target.value)}
                    placeholder="150.00"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', color: '#374151', fontWeight: '600' }}>
                    Package Size (videos)
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={adminCounterPackageSize}
                    onChange={(e) => setAdminCounterPackageSize(e.target.value)}
                    placeholder={batch?.batchSize?.toString() || '4'}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              
              {adminCounterPrice && (
                <div style={{ marginTop: '12px', padding: '12px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                  <p style={{ color: '#0369a1', margin: '0', fontSize: '14px', fontWeight: '600' }}>
                    Your counter offer: ${adminCounterPrice} × {adminCounterPackageSize || batch?.batchSize} videos = ${((parseFloat(adminCounterPrice) || 0) * (parseInt(adminCounterPackageSize) || batch?.batchSize || 0)).toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCounterOfferModal(false);
                  setAdminCounterPrice('');
                  setAdminCounterPackageSize('');
                }}
                disabled={processingAction}
                style={{
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  opacity: processingAction ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleMakeCounterOffer}
                disabled={processingAction || !adminCounterPrice}
                style={{
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  opacity: (processingAction || !adminCounterPrice) ? 0.6 : 1
                }}
              >
                {processingAction ? 'Sending...' : 'Send Counter Offer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Topic Input Modal */}
      {showTopicModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginTop: 0, color: '#10b981' }}>🎬 Start Collaboration</h3>
            
            <div style={{ marginBottom: '20px' }}>
              <p style={{ color: '#6b7280', marginBottom: '16px' }}>
                Enter the video topic/subject for this collaboration (optional):
              </p>
              
              <textarea
                value={topicInput}
                onChange={(e) => setTopicInput(e.target.value)}
                placeholder="e.g., Product review, Tutorial, Brand showcase..."
                style={{
                  width: '100%',
                  height: '100px',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
              
              <div style={{ marginTop: '8px', color: '#9ca3af', fontSize: '14px' }}>
                This topic will be included in the script request email to the creator.
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowTopicModal(false);
                  setSelectedCollaborationId('');
                  setTopicInput('');
                }}
                disabled={processingAction}
                style={{
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  opacity: processingAction ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmStartCollaboration}
                disabled={processingAction}
                style={{
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  opacity: processingAction ? 0.6 : 1
                }}
              >
                {processingAction ? 'Starting...' : 'Start Collaboration'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Batch Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginTop: 0, color: '#8b5cf6' }}>✏️ Edit Batch</h3>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', color: '#374151', fontWeight: '600' }}>
                  Creator Email
                </label>
                <input
                  type="email"
                  value={editForm.creatorEmail}
                  onChange={(e) => handleEditFormChange('creatorEmail', e.target.value)}
                  placeholder="email@example.com"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {(batch as any)?.pricingType === 'rpm' ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', color: '#374151', fontWeight: '600' }}>
                        RPM Rate ($)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editForm.rpmRate}
                        onChange={(e) => handleEditFormChange('rpmRate', e.target.value)}
                        placeholder="5.00"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '16px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', color: '#374151', fontWeight: '600' }}>
                        Max Price ($)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={editForm.maxPrice}
                        onChange={(e) => handleEditFormChange('maxPrice', e.target.value)}
                        placeholder="500"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '16px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: '#374151', fontWeight: '600' }}>
                    Price per Video ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={editForm.batchPrice}
                    onChange={(e) => handleEditFormChange('batchPrice', e.target.value)}
                    placeholder="100"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', color: '#374151', fontWeight: '600' }}>
                    Batch Size (videos)
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={editForm.batchSize}
                    onChange={(e) => handleEditFormChange('batchSize', e.target.value)}
                    placeholder="4"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEditModal(false)}
                disabled={processingAction}
                style={{
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  opacity: processingAction ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={processingAction}
                style={{
                  background: '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  opacity: processingAction ? 0.6 : 1
                }}
              >
                {processingAction ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchDetail;